import { Grid, Autocomplete, TextField, Checkbox } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ReactHookFormTextField from '../ReactHookForm/ReactHookFormTextField';
import FormDialog from '../Dialogs/FormDialog';
import { useHEMTUsers } from '../../hooks/api/useHEMTUsers';
import { useForecasters } from '../../hooks/api/forecasters';
import { useCapacityTypes } from '../../hooks';
import WorkingUnitLocationFormFields from '../General/WorkingUnitLocationFormFields';
import ReactHookFormSelect from '../ReactHookForm/ReactHookFormSelect';
import ReactHookFormCheckbox from '../ReactHookForm/ReactHookFormCheckbox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const StrategicVolumeDialog = ({ showDialog, strategicVolume, onSubmit, onClose }) => {
    const { t } = useTranslation('bidding');
    const { data: forecasters } = useForecasters();
    const { data: users } = useHEMTUsers();
    const [capacityTypes] = useCapacityTypes();

    // Create hybrid options list: forecasters first, then users
    const hybridOptions = useMemo(() => {
        // Get forecaster user IDs to avoid duplicates
        const forecasterUserIds = new Set((forecasters || []).filter((f) => f.userId).map((f) => f.userId));

        // Map forecasters with source metadata
        const forecasterOptions = (forecasters || []).map((f) => ({
            ...f,
            source: 'forecaster',
            displayName: f.name,
        }));

        // Map users (excluding those already in forecasters)
        const userOptions = (users || [])
            .filter((u) => !forecasterUserIds.has(u.id))
            .map((u) => ({
                ...u,
                source: 'user',
                displayName: u.name,
            }));

        return [...forecasterOptions, ...userOptions];
    }, [forecasters, users]);
    const form = useForm({
        defaultValues: {
            id: strategicVolume?.id || '',
            location: strategicVolume?.location?.id || '',
            workingUnitLocation: strategicVolume?.workingUnitLocation
                ? {
                      ...strategicVolume?.workingUnitLocation,
                      label: `${strategicVolume?.workingUnitLocation?.workingUnit?.worktype?.worktype} - ${strategicVolume?.workingUnitLocation?.workingUnit?.channel?.name} - ${strategicVolume?.workingUnitLocation?.workingUnit?.task?.name}`,
                  }
                : '',
            forecasters: strategicVolume?.forecasters || [],
            capacityType: strategicVolume?.capacityType?.id || '',
            unit: strategicVolume?.capacityType?.unit || '',
            comment: strategicVolume?.comment || '',
            contact: strategicVolume?.contact || '',
            active: !strategicVolume || strategicVolume?.active === true,
        },
    });
    const {
        control,
        formState: { errors },
        watch,
    } = form;
    const selectedCapacityTypeValue = watch('capacityType');
    const selectedCapacityType = capacityTypes.find((ct) => ct.id === selectedCapacityTypeValue);

    useEffect(() => {
        form.setValue('unit', selectedCapacityType?.unit, { shouldValidate: true });
    }, [form, selectedCapacityType]);

    // Update forecasters with proper source metadata when editing
    useEffect(() => {
        if (strategicVolume?.forecasters && hybridOptions.length > 0) {
            const mappedForecasters = strategicVolume.forecasters
                .map((f) => {
                    // Find matching option in hybrid list
                    return hybridOptions.find((opt) => opt.id === f.id && opt.source === 'forecaster');
                })
                .filter(Boolean);

            if (mappedForecasters.length > 0) {
                form.setValue('forecasters', mappedForecasters);
            }
        }
    }, [strategicVolume?.forecasters, hybridOptions, form]);

    return (
        <FormDialog
            title={strategicVolume?.id ? t('sv.editTitle') : t('sv.addNew')}
            showDialog={showDialog}
            form={form}
            onSubmit={onSubmit}
            onClose={onClose}
        >
            <WorkingUnitLocationFormFields form={form} isWorkingUnitMultiSelect={false}></WorkingUnitLocationFormFields>
            <Grid item xs={12}>
                <ReactHookFormSelect
                    name="capacityType"
                    options={capacityTypes}
                    mainProp="name"
                    label={t('field.capacityType')}
                    control={control}
                    rules={{ required: { value: true, message: t('validation.required') } }}
                    formErrors={errors}
                    isMultiSelect={false}
                ></ReactHookFormSelect>
            </Grid>
            <Grid item xs={12}>
                <ReactHookFormTextField
                    name="unit"
                    control={control}
                    label={t('field.unit')}
                    formErrors={errors}
                    disabled={true}
                    rules={{ required: { value: true, message: t('validation.required') } }}
                ></ReactHookFormTextField>
            </Grid>
            <Grid item xs={12}>
                <Controller
                    name="forecasters"
                    control={control}
                    rules={{ required: { value: true, message: t('validation.required') } }}
                    render={({ field, fieldState }) => (
                        <Autocomplete
                            {...field}
                            multiple
                            options={hybridOptions}
                            groupBy={(option) =>
                                option.source === 'forecaster'
                                    ? t('sv.groupExistingForecasters')
                                    : t('sv.groupOtherUsers')
                            }
                            getOptionLabel={(option) => option.displayName || option.name}
                            isOptionEqualToValue={(option, value) =>
                                option.source === value.source && option.id === value.id
                            }
                            value={field.value || []}
                            onChange={(event, newValue) => field.onChange(newValue)}
                            renderOption={(props, option, { selected }) => (
                                <li {...props} key={option.source + '-' + option.id}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    <span>{option.displayName || option.name}</span>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={`${t('field.forecaster')} *`}
                                    variant="outlined"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                            noOptionsText={t('common.noOptions')}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12}>
                <ReactHookFormTextField
                    name="contact"
                    control={control}
                    label={t('field.contactType')}
                    formErrors={errors}
                ></ReactHookFormTextField>
            </Grid>
            <Grid item xs={12}>
                <ReactHookFormTextField
                    name="comment"
                    control={control}
                    label={t('field.note')}
                    formErrors={errors}
                ></ReactHookFormTextField>
            </Grid>
            <Grid item xs={12}>
                <ReactHookFormCheckbox
                    name="active"
                    control={control}
                    label={t('field.active')}
                    labelPlacement="end"
                ></ReactHookFormCheckbox>
            </Grid>
        </FormDialog>
    );
};

export default StrategicVolumeDialog;
